import { useEffect, useState } from "react";
import api, { formatDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { MapPin, Plus, CheckCircle2, Calendar as CalendarIcon } from "lucide-react";
import { toast } from "sonner";

const PURPOSES = ["Document collection", "Property verification", "KYC verification", "Field inspection", "Final signing"];

export default function Visits() {
    const { user } = useAuth();
    const [visits, setVisits] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        customer_id: "", purpose: PURPOSES[0], scheduled_at: "", notes: "",
    });

    const load = async () => {
        try {
            const [v, c] = await Promise.all([api.get("/visits"), api.get("/customers").catch(() => ({ data: [] }))]);
            setVisits(v.data); setCustomers(c.data);
        } catch (e) { /* silent */ }
    };
    useEffect(() => { load(); }, []);

    const create = async (e) => {
        e.preventDefault();
        try {
            const iso = new Date(form.scheduled_at).toISOString();
            await api.post("/visits", { ...form, scheduled_at: iso });
            toast.success("Visit scheduled");
            setOpen(false);
            setForm({ customer_id: "", purpose: PURPOSES[0], scheduled_at: "", notes: "" });
            load();
        } catch (err) { toast.error(formatDetail(err.response?.data?.detail)); }
    };

    const checkIn = async (id) => {
        try {
            let coords = {};
            if (navigator.geolocation) {
                await new Promise((res) => {
                    navigator.geolocation.getCurrentPosition(
                        (p) => { coords = { latitude: p.coords.latitude, longitude: p.coords.longitude }; res(); },
                        () => res(), { timeout: 3000 }
                    );
                });
            }
            await api.post(`/visits/${id}/check-in`, { ...coords, outcome: "completed" });
            toast.success("Checked in");
            load();
        } catch (err) { toast.error(formatDetail(err.response?.data?.detail)); }
    };

    const today = new Date().toISOString().slice(0, 10);
    const upcoming = visits.filter((v) => v.status === "scheduled");
    const done = visits.filter((v) => v.status !== "scheduled");

    return (
        <div className="space-y-6" data-testid="visits-page">
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-[10px] uppercase tracking-[0.28em] text-komma-gold">Field Ops</p>
                    <h1 className="font-serif text-3xl text-komma-navy">Field Visits</h1>
                    <p className="text-sm text-gray-600 mt-1">Schedule visits, capture GPS on check-in, keep audit trail per loan.</p>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button className="bg-komma-navy hover:bg-komma-navy-light" data-testid="add-visit-btn">
                            <Plus size={16} className="mr-2" /> Schedule Visit
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg">
                        <DialogHeader><DialogTitle className="font-serif text-komma-navy">New Field Visit</DialogTitle></DialogHeader>
                        <form onSubmit={create} className="space-y-3">
                            <div>
                                <Label className="text-xs uppercase tracking-widest text-komma-navy">Customer</Label>
                                <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                                    <SelectTrigger className="mt-1.5" data-testid="visit-customer"><SelectValue placeholder="Select customer" /></SelectTrigger>
                                    <SelectContent>
                                        {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.phone}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-widest text-komma-navy">Purpose</Label>
                                <Select value={form.purpose} onValueChange={(v) => setForm({ ...form, purpose: v })}>
                                    <SelectTrigger className="mt-1.5" data-testid="visit-purpose"><SelectValue /></SelectTrigger>
                                    <SelectContent>{PURPOSES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-widest text-komma-navy">Date & Time</Label>
                                <Input type="datetime-local" required min={`${today}T00:00`}
                                    value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })}
                                    className="mt-1.5 h-10" data-testid="visit-datetime" />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-widest text-komma-navy">Notes</Label>
                                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" data-testid="visit-notes" />
                            </div>
                            <Button type="submit" className="w-full bg-komma-navy hover:bg-komma-navy-light" data-testid="visit-submit">Schedule</Button>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            <div>
                <h2 className="text-xs uppercase tracking-widest text-gray-500 mb-3">Upcoming ({upcoming.length})</h2>
                {upcoming.length === 0 ? (
                    <div className="komma-card p-8 text-center text-gray-500 text-sm">No visits scheduled.</div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                        {upcoming.map((v) => (
                            <div key={v.id} className="komma-card p-5" data-testid={`visit-${v.id}`}>
                                <div className="flex items-start justify-between">
                                    <div>
                                        <p className="font-serif text-lg text-komma-navy">{v.customer_name}</p>
                                        <p className="text-xs text-gray-500">{v.customer_phone}</p>
                                    </div>
                                    <Badge variant="outline" className="bg-komma-gold/10 border-komma-gold/40 text-komma-navy">Scheduled</Badge>
                                </div>
                                <div className="komma-gold-hairline my-3" />
                                <p className="text-sm text-komma-navy font-semibold">{v.purpose}</p>
                                <p className="text-xs text-gray-500 mt-1 flex items-center gap-1"><CalendarIcon size={12} /> {new Date(v.scheduled_at).toLocaleString()}</p>
                                {v.notes && <p className="text-xs text-gray-600 mt-2 italic">&ldquo;{v.notes}&rdquo;</p>}
                                {(user.role === "agent" || user.role === "ceo" || user.role === "office_admin") && (
                                    <Button size="sm" onClick={() => checkIn(v.id)} data-testid={`checkin-${v.id}`}
                                        className="mt-4 w-full bg-komma-gold hover:bg-komma-gold-dark text-komma-navy">
                                        <MapPin size={13} className="mr-2" /> Check-in Now
                                    </Button>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {done.length > 0 && (
                <div>
                    <h2 className="text-xs uppercase tracking-widest text-gray-500 mb-3">Completed ({done.length})</h2>
                    <div className="komma-card divide-y divide-gray-100">
                        {done.map((v) => (
                            <div key={v.id} className="p-4 flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-semibold text-komma-navy">{v.customer_name}</p>
                                    <p className="text-xs text-gray-500">{v.purpose} · {new Date(v.scheduled_at).toLocaleString()}</p>
                                </div>
                                <div className="flex items-center gap-2 text-emerald-700 text-xs">
                                    <CheckCircle2 size={14} />
                                    {v.checked_in_at ? `Checked in ${new Date(v.checked_in_at).toLocaleDateString()}` : "Completed"}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
